import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, Store as StoreIcon, Heart, Eye, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import UMKMForm, { UMKM_EMPTY } from './UMKMForm';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };

const STATUS = {
  pending: { label: 'Menunggu', cls: 'bg-amber-50 text-amber-700', Icon: Clock },
  approved: { label: 'Tayang', cls: 'bg-emerald-50 text-emerald-700', Icon: CheckCircle2 },
  rejected: { label: 'Ditolak', cls: 'bg-red-50 text-red-700', Icon: XCircle },
};

const UMKMManager = () => {
  const { user } = useAuth();
  const [ads, setAds] = useState([]);
  const [filter, setFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(UMKM_EMPTY);

  const load = () => {
    const params = { mosque_id: user?.mosque_id };
    if (filter !== 'all') params.status = filter;
    axios.get(`${API_URL}/api/admin/umkm`, { ...cfg, params }).then((r) => setAds(r.data)).catch(() => {});
  };
  useEffect(load, [filter]);

  const openNew = () => { setEditing(null); setForm(UMKM_EMPTY); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ name: a.name, description: a.description, category: a.category, location: a.location || '', whatsapp: a.whatsapp, video_url: a.video_url || '', images: a.images || [], logo_url: a.logo_url || '' });
    setOpen(true);
  };
  const submit = async (payload) => {
    try {
      if (editing) { await axios.put(`${API_URL}/api/umkm/${editing.id}`, payload, cfg); toast.success('Iklan diperbarui'); }
      else { await axios.post(`${API_URL}/api/umkm`, payload, cfg); toast.success('Iklan ditambahkan & langsung tayang'); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Gagal menyimpan'); }
  };
  const del = async (id) => { if (!window.confirm('Hapus iklan ini?')) return; try { await axios.delete(`${API_URL}/api/umkm/${id}`, cfg); toast.success('Iklan dihapus'); load(); } catch (e) { toast.error('Gagal menghapus'); } };
  const approve = async (id) => { try { await axios.post(`${API_URL}/api/umkm/${id}/approve`, {}, cfg); toast.success('Iklan disetujui'); load(); } catch (e) { toast.error('Gagal menyetujui'); } };
  const reject = async (id) => { try { await axios.post(`${API_URL}/api/umkm/${id}/reject`, {}, cfg); toast.success('Iklan ditolak'); load(); } catch (e) { toast.error('Gagal menolak'); } };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-primary-dark">Moderasi Pasar UMKM</h2>
          <p className="text-sm font-body text-text-secondary mt-1">Setujui iklan merchant & kelola toko</p>
        </div>
        <button data-testid="add-umkm-btn" onClick={openNew} className="flex items-center gap-2 px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full font-medium"><Plus className="w-5 h-5" /> Tambah Iklan</button>
      </div>

      <div className="flex gap-2 mb-5">
        {[['all', 'Semua'], ['pending', 'Menunggu'], ['approved', 'Tayang'], ['rejected', 'Ditolak']].map(([f, l]) => (
          <button key={f} data-testid={`umkm-filter-${f}`} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-full text-sm font-body font-medium ${filter === f ? 'bg-emerald-700 text-white' : 'bg-white border border-stone-200 text-stone-600'}`}>{l}</button>
        ))}
      </div>

      {ads.length === 0 ? (
        <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
          <StoreIcon className="w-16 h-16 text-stone-300 mx-auto mb-4" />
          <p className="text-text-secondary font-body">Tidak ada iklan pada filter ini</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {ads.map((a) => {
            const st = STATUS[a.status] || STATUS.pending; const SIcon = st.Icon;
            return (
              <motion.div key={a.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-stone-200 p-4" data-testid={`umkm-item-${a.id}`}>
                <div className="flex gap-3 mb-3">
                  <div className="w-16 h-16 rounded-xl bg-cover bg-center bg-stone-100 flex-shrink-0" style={{ backgroundImage: a.logo_url ? `url('${a.logo_url}')` : 'none' }}>
                    {!a.logo_url && <div className="w-full h-full flex items-center justify-center"><StoreIcon className="w-8 h-8 text-stone-400" /></div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-body font-semibold text-text-primary truncate">{a.name}</h3>
                    <span className="inline-block px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[11px] font-medium my-0.5">{a.category}</span>
                    <span className={`flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium w-fit ${st.cls}`}><SIcon className="w-3 h-3" /> {st.label}</span>
                  </div>
                </div>
                <p className="text-sm font-body text-text-secondary line-clamp-2 mb-2">{a.description}</p>
                <div className="flex items-center gap-4 mb-3 text-xs font-body text-stone-500">
                  <span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5 text-rose-500" /> {a.likes || 0}</span>
                  <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {a.view_count || 0}</span>
                </div>
                {a.status === 'pending' && (
                  <div className="flex gap-2 mb-2">
                    <button data-testid={`approve-umkm-${a.id}`} onClick={() => approve(a.id)} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-medium"><CheckCircle2 className="w-4 h-4" /> Setujui</button>
                    <button data-testid={`reject-umkm-${a.id}`} onClick={() => reject(a.id)} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-xl text-sm font-medium"><XCircle className="w-4 h-4" /> Tolak</button>
                  </div>
                )}
                <div className="flex gap-2">
                  <button data-testid={`edit-umkm-${a.id}`} onClick={() => openEdit(a)} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-xl text-sm font-medium"><Edit2 className="w-4 h-4" /> Edit</button>
                  <button data-testid={`delete-umkm-${a.id}`} onClick={() => del(a.id)} className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-xl"><Trash2 className="w-4 h-4" /></button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
      <UMKMForm open={open} formData={form} setFormData={setForm} onSubmit={submit} onClose={() => setOpen(false)} editing={editing} />
    </div>
  );
};

export default UMKMManager;
